import React, { useEffect, useContext, useState, useMemo } from "react"
import CoachHowTos from "./CoachHowTos"
import NavShell from "./NavShell"
import useName from "../../hooks/useName"
import UserButton from "./UserButton"
import UserButtonMobile from "./UserButtonMobile"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Button } from "../ui/button"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { getUserColor } from "../../utils/getUserColor"
import { getTextColor } from "../../utils/getTextColor"
import { Link, useLocation } from "react-router-dom"
import { NotificationCtx } from "../../context/notificationContext"
import { NavLinkItem } from "./NavLinkItem"
import {
  SvgBilling,
  SvgCalendar,
  SvgChat,
  SvgClose,
  SvgHamburger,
  SvgHome,
  SvgListCheck,
  SvgSettingsSlider,
  SvgUser3,
  SvgVideoOutline,
} from "../icons/"
import { UserCtx } from "../../context/userContext"
import { SvgZoeeGradientFavicon } from "../icons/logos/zoee-gradient-favicon"
import { SvgZoeeGradientLogo } from "../icons/logos/zoee-gradient-logo"
import { NotificationBell } from "./notification-bell"

interface Props {
  guideOpened: any
  setGuideOpened: any
}

const CoachAuthedNav: React.FC<Props> = ({ guideOpened, setGuideOpened }) => {
  const { user } = useContext(UserCtx)
  const { initial } = useName()
  const location = useLocation()
  const { previewImgUrl } = useContext(CommonFunctionCtx)
  const { notificationAlert } = useContext(NotificationCtx)
  const {
    inboxNotifications,
    setInboxNotifications,
    contactsNotifications,
    setContactsNotifications,
    notifications,
    getNotifications,
  } = useContext(NotificationCtx)
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false)
  const [showUserButton, setShowUserButton] = useState<boolean>(false)
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false)
  const createdAt = new Date(user.createdAt)
  createdAt.setMonth(createdAt.getMonth() + 1)

  const isCoach = user.activeProfile === "coach"
  const unreadInboxNotificationNumber = useMemo(() => {
    return inboxNotifications.filter((n: any) => !n.viewed).length
  }, [inboxNotifications])

  const unreadContactsNotificationNumber = useMemo(() => {
    return contactsNotifications.filter((n: any) => !n.viewed).length
  }, [contactsNotifications])

  useEffect(() => {
    // Count messages and connection request notifications
    setInboxNotifications(
      notifications.filter((n) => n.notification_category === "messages")
    )
    setContactsNotifications(
      notifications.filter((n) => {
        return n.slug === "received_connection_request" && n.viewed === 0
      })
    )
  }, [])

  useEffect(() => {
    getNotifications()
  }, [notificationAlert.show])

  const isMobile = window.innerWidth <= 642

  return (
    <>
      <NavShell
        className="flex items-center justify-between h-20"
        outerClassName="hidden lg:flex z-[1000]"
      >
        <Link to="/">
          <SvgZoeeGradientFavicon />
        </Link>

        <div className="hidden items-center lg:flex lg:gap-2">
          <NavLinkItem to="/coach/dashboard">
            <SvgHome />
            Home
          </NavLinkItem>
          <NavLinkItem
            to="/coach/inbox"
            notifications={unreadInboxNotificationNumber}
          >
            <SvgChat />
            Inbox
          </NavLinkItem>
          <NavLinkItem
            to="/coach/contacts"
            notifications={unreadContactsNotificationNumber}
          >
            <SvgUser3 />
            Contacts
          </NavLinkItem>
          <NavLinkItem to="/coach/scheduling">
            <SvgCalendar />
            Calendar
          </NavLinkItem>
          <NavLinkItem to="/coach/sessions">
            <SvgVideoOutline />
            Sessions
          </NavLinkItem>
          <NavLinkItem to="/coach" end>
            <SvgListCheck />
            Manage
          </NavLinkItem>
        </div>

        <div className="relative flex items-center justify-end gap-4">
          <NotificationBell />

          <UserButton
            guideOpened={guideOpened}
            setGuideOpened={setGuideOpened}
          />
        </div>
      </NavShell>

      {/* Mobile */}
      <div className="sticky top-2.5 z-[1000] mx-2.5 mb-10 flex lg:hidden">
        <div
          className="relative mx-auto flex h-20 w-full max-w-screen-xl items-center justify-between rounded-lg bg-white px-2.5 py-5 sm:px-4"
          style={{
            boxShadow:
              "0 10px 20px -5px rgba(111, 89, 246, 0.15), 0 -5px 10px -5px rgba(111, 89, 246, 0.15), 0 6px 10px -5px rgba(111, 89, 246, 0.10)",
          }}
        >
          <Button
            variant="ghost"
            className="ml-4 lg:hidden"
            size="icon"
            onClick={() => setShowMobileNav(!showMobileNav)}
          >
            {showMobileNav ? <SvgClose /> : <SvgHamburger />}
          </Button>

          {showMobileNav && (
            <div className="zoee-backdrop-filter fixed left-0 top-0 z-[9999] h-screen w-screen"></div>
          )}

          <div
            className={` z-[9999] fixed left-0 top-0 flex h-screen flex-col overflow-y-auto rounded-lg bg-white bg-white p-8 transition-all duration-500 ease-in-out ${
              showMobileNav ? "left-0 w-[279px]" : "left-[-279px] w-0"
            }`}
            style={{
              boxShadow:
                "5px 0 15px -2px rgba(111, 89, 246, 0.15), 5px 0 15px -2px rgba(111, 89, 246, 0.15), 5px 0 15px -2px rgba(111, 89, 246, 0.1)",
            }}
            onClick={() => setShowMobileNav(!showMobileNav)}
          >
            <Button
              className="bg-grayFlash absolute right-[20px] top-[25px] rounded-full"
              size="icon"
              variant="ghost"
              onClick={() => setShowMobileNav(!showMobileNav)}
            >
              <SvgClose />
            </Button>
            <Link
              to="/"
              onClick={() => setShowMobileNav(false)}
              className="mb-[48px] mt-[10px]"
            >
              <SvgZoeeGradientLogo className="h-auto w-[155px]" />
            </Link>
            <div className="mb-[56px] flex flex-col gap-[20px] text-center text-[18px]">
              <NavLinkItem to="/coach/dashboard">
                <SvgHome />
                Home
              </NavLinkItem>
              <NavLinkItem
                to="/coach/inbox"
                notifications={unreadInboxNotificationNumber}
              >
                <SvgChat />
                Inbox
              </NavLinkItem>
              <NavLinkItem
                to="/coach/contacts"
                notifications={unreadContactsNotificationNumber}
              >
                <SvgUser3 />
                Contacts
              </NavLinkItem>
              <NavLinkItem to="/coach/services">
                <SvgBilling />
                Services
              </NavLinkItem>
              <NavLinkItem to="/coach/scheduling">
                <SvgCalendar />
                Calendar
              </NavLinkItem>
              <NavLinkItem to="/coach/sessions">
                <SvgVideoOutline />
                Sessions
              </NavLinkItem>
              <NavLinkItem to="/coach" end>
                <SvgSettingsSlider />
                Manage
              </NavLinkItem>
            </div>
          </div>

          <div className="relative mr-2 flex items-center justify-end gap-4">
            <NotificationBell />

            {showUserButton ? (
              <SvgClose />
            ) : (
              <Button
                variant="ghost"
                className="h-10 w-10 rounded-full"
                onClick={() => setShowUserButton(() => !showUserButton)}
              >
                <Avatar size="xs">
                  {Boolean(user.hasAvatar) && (
                    <AvatarImage
                      alt="avatar"
                      src={previewImgUrl || user.avatar_url}
                    />
                  )}
                  <AvatarFallback className={getUserColor(user.userColor)}>
                    <div
                      style={{
                        color: getTextColor(user.userColor),
                      }}
                    >
                      {initial}
                    </div>
                  </AvatarFallback>
                </Avatar>
              </Button>
            )}
            <UserButtonMobile
              setShowUserButton={setShowUserButton}
              showUserButton={showUserButton}
              guideOpened={guideOpened}
              setGuideOpened={setGuideOpened}
            />
          </div>
        </div>
      </div>
      <div className="hidden h-24 md:block"></div>
      {isCoach &&
      guideOpened &&
      location.pathname !== "/coach/edit-website" &&
      (location.pathname !== "/account-settings" || !isMobile) ? (
        <div
          className={` ${
            isSidePanelOpen ? "left-0" : "-left-[300px]"
          } hover:shadow-custom fixed top-[100px] md:top-[150px] z-[1001] flex h-[550px] w-[300px] cursor-pointer items-center justify-center rounded-xl transition-all duration-500 ease-in-out`}
          style={{ backgroundColor: "#4750F5" }}
        >
          <CoachHowTos />
          <div
            onClick={() => setIsSidePanelOpen(!isSidePanelOpen)}
            className={`shadow-outline } fixed sm:top-[606px] md:top-[295px] z-[1000] -mr-[349px] flex h-[50px] w-[155px] -rotate-90 transform cursor-pointer items-center justify-center rounded-bl-lg rounded-br-lg
          `}
            style={{ backgroundColor: "#4750F5" }}
          >
            {isSidePanelOpen ? (
              <h1 className="text-sm font-bold text-white">
                <span>Close</span>
              </h1>
            ) : (
              <h1 className="text-sm font-bold text-white">
                <span>How</span>
                <span className="ml-2">To's</span>
              </h1>
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}

export default CoachAuthedNav
